import type { InstalledComponent, VersionState } from 'features/versions/types'

/**
 * Defines the contract for the App API, ensuring consistency between
 * the Electron and Browser implementations.
 */
interface AppAPI {
  getInstalledComponents(): Promise<InstalledComponent[]>
  deleteComponents(components: string[]): Promise<string[]>
  getVersionStatus(versionId: string): Promise<VersionState>
  getVersionComponentsInstalled(versionId: string): Promise<Record<string, boolean>>
  extractVersionInfo(filePath: string): Promise<{
    installOrder: string[]
    components: string[]
    version: string
  }>
  getAppVersion(): Promise<string>
  getAllVersionStates(): Promise<Record<number, VersionState>>

  // Event-based APIs
  onVersionStateUpdate(versionId: string, callback: (state: VersionState) => void): () => void
  onAllVersionStatesUpdate(callback: (states: Record<number, VersionState>) => void): () => void
  startInstall(versionId: string): void
  cancelInstall(versionId: string): void
}

/**
 * Proxy/Adapter for IPC communication
 * Abstracts Electron implementation with room for future Tauri migration
 */
class ElectronAPI implements AppAPI {
  async getInstalledComponents() {
    return window.api!.getInstalledComponents()
  }

  async deleteComponents(components: string[]) {
    return window.api!.deleteComponents(components)
  }

  async getVersionStatus(versionId: string) {
    return window.api!.getVersionStatus(versionId)
  }

  async getVersionComponentsInstalled(versionId: string) {
    return window.api!.getVersionComponentsInstalled(versionId)
  }

  async extractVersionInfo(filePath: string) {
    return window.api!.extractVersionInfo(filePath)
  }

  async getAppVersion() {
    return window.api!.getAppVersion()
  }

  async getAllVersionStates() {
    return window.api!.getAllVersionStates()
  }

  onVersionStateUpdate(versionId: string, callback: (state: VersionState) => void) {
    const channelName = `update-version-state--${versionId}`
    const listener = (_event: unknown, state: VersionState) => callback(state)
    const removeListener = window.electron!.ipcRenderer.on(channelName, listener)
    window.electron!.ipcRenderer.send('get-version-state', versionId)
    return removeListener || (() => {})
  }

  onAllVersionStatesUpdate(callback: (states: Record<number, VersionState>) => void) {
    const listener = (_event: unknown, states: Record<number, VersionState>) => callback(states)
    const removeListener = window.electron!.ipcRenderer.on('all-version-states-update', listener)
    return removeListener || (() => {})
  }

  startInstall(versionId: string) {
    window.electron!.ipcRenderer.send('start-install', { versionId })
  }

  cancelInstall(versionId: string) {
    window.electron!.ipcRenderer.send('cancel-install', versionId)
  }
}

class BrowserAPI implements AppAPI {
  async getInstalledComponents() {
    return []
  }

  async deleteComponents(_components: string[]) {
    return []
  }

  async getVersionStatus(_versionId: string) {
    return { status: 'notInstalled' as const }
  }

  async getVersionComponentsInstalled(_versionId: string) {
    return {}
  }

  async extractVersionInfo(_filePath: string) {
    return {
      installOrder: [],
      components: [],
      version: '',
    }
  }

  async getAppVersion() {
    return 'PRESYNC_IN_BROWSER'
  }

  async getAllVersionStates() {
    return {}
  }

  onVersionStateUpdate(_versionId: string, _callback: (state: VersionState) => void) {
    return () => {}
  }

  onAllVersionStatesUpdate(_callback: (states: Record<number, VersionState>) => void) {
    return () => {}
  }

  startInstall(_versionId: string) {
    console.warn('startInstall: Not running in Electron')
  }

  cancelInstall(_versionId: string) {
    console.warn('cancelInstall: Not running in Electron')
  }
}

// Runtime detection
export const isApp = typeof window !== 'undefined' && 'api' in window
export const isBrowser = !isApp

export const appAPI: AppAPI = isApp ? new ElectronAPI() : new BrowserAPI()
